"use client"

import { useState, useEffect } from "react"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import { StatusTemplate } from "@/types/view-templates"

const STATUS_TEMPLATE_REFRESH_EVENT = "losi:space-status-template-refresh"

const DEFAULT_STATUS_TEMPLATE: StatusTemplate = {
  id: "basic",
  name: "Basic (To Do, In Progress, Done)",
  description: "Simple three-stage workflow",
  isDefault: true,
  statuses: [
    {
      id: "todo",
      name: "To Do",
      color: "slate",
      order: 0,
      statusValues: ["todo", "pending", "backlog", "new"]
    },
    {
      id: "in_progress",
      name: "In Progress",
      color: "blue",
      order: 1,
      statusValues: ["in_progress", "inprogress", "doing", "active", "started"]
    },
    {
      id: "done",
      name: "Done",
      color: "green",
      order: 2,
      statusValues: ["done", "completed", "finished", "closed"]
    }
  ]
}

const normalizeStatusValue = (value: string) =>
  value.toLowerCase().replace(/[-\s]/g, "_")

const getStatusValuesFromTemplate = (template: StatusTemplate) => {
  const values = template.statuses.flatMap((status) => {
    if (status.statusValues?.length) {
      return status.statusValues
    }

    return [status.id]
  })

  return Array.from(new Set(values.map(normalizeStatusValue)))
}

export const useSpaceStatusTemplate = (spaceId: string) => {
  const [statusTemplate, setStatusTemplate] = useState<StatusTemplate | null>(null)
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const fetchStatusTemplate = async () => {
    if (!spaceId) {
      setIsLoading(false)
      return
    }

    try {
      const response = await authenticatedFetch(`/api/spaces/${spaceId}`, {
        cache: "no-store"
      })

      if (!response.ok) {
        throw new Error("Failed to load status template")
      }

      const spaceData = (await response.json()) as {
        status_template?: StatusTemplate | null
      } | null

      if (spaceData?.status_template?.statuses?.length) {
        const nextTemplate = spaceData.status_template as StatusTemplate
        setStatusTemplate(nextTemplate)
        setAvailableStatuses(getStatusValuesFromTemplate(nextTemplate))
      } else {
        setStatusTemplate(DEFAULT_STATUS_TEMPLATE)
        setAvailableStatuses(getStatusValuesFromTemplate(DEFAULT_STATUS_TEMPLATE))
      }
    } catch (error) {
      console.error("Error fetching status template:", error)
      setStatusTemplate((currentTemplate) =>
        currentTemplate || DEFAULT_STATUS_TEMPLATE
      )
      setAvailableStatuses((currentStatuses) =>
        currentStatuses.length > 0
          ? currentStatuses
          : getStatusValuesFromTemplate(DEFAULT_STATUS_TEMPLATE)
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchStatusTemplate()
  }, [spaceId, refreshTrigger])

  useEffect(() => {
    const handleRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ spaceId?: string }>).detail
      if (!detail?.spaceId || detail.spaceId === spaceId) {
        setRefreshTrigger(prev => prev + 1)
      }
    }

    window.addEventListener(STATUS_TEMPLATE_REFRESH_EVENT, handleRefresh)
    return () => {
      window.removeEventListener(STATUS_TEMPLATE_REFRESH_EVENT, handleRefresh)
    }
  }, [spaceId])

  const refresh = () => {
    setRefreshTrigger(prev => prev + 1)
    window.dispatchEvent(
      new CustomEvent<{ spaceId?: string }>(STATUS_TEMPLATE_REFRESH_EVENT, {
        detail: { spaceId }
      })
    )
  }

  return {
    statusTemplate,
    availableStatuses,
    isLoading,
    refresh
  }
}
